import React, { useEffect, useRef, useState } from 'react';

import { Text, Checkbox, Icon, SearchBox, FontIcon } from '@fluentui/react';

import { useQueryAllDeviceList } from 'app/services/request-api';
import { DeviceCategory } from 'generated/request-backend';
import { IOptionProps } from '../../models';
import { keyPress } from 'app/components/helpers';
import { StringConstants } from 'app/utils/constants';
import useStore, { IStore } from 'app/store';
import { IStateData } from 'app/models/common/post';
interface IProps {
    selectedKeys: IOptionProps[];
    setSelectedKeys: Function;
    isListView: boolean;
    stateData?: IStateData;
    setFilterActive: (filterActive: boolean) => void;
}

const ProductsFilter: React.FC<IProps> = ({ selectedKeys, setSelectedKeys, isListView, stateData, setFilterActive }) => {
    const { data, isLoading, isError } = useQueryAllDeviceList();
    const filterPanelDisabled = useStore((state: IStore) => state.filterPanelDisabled);

    const [options, setOptions] = useState<IOptionProps[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [searchedResults, setSearchedResults] = useState<IOptionProps[]>([]);

    const dropDownRef = useRef<HTMLDivElement>(null);

    const setQueryStateData = (data: IStateData) => {
        setSelectedKeys(data.productsSelection);
    };

    const onChange = (key: number, dependentProperties?: number[], parentProperty?: number) => {
        if (key === 0) {
            if (selectedKeys.filter((item) => item.key === key).length === 0) {
                let arr: IOptionProps[] = [];
                options.forEach((ele) => arr.push(ele));
                setQueryStateData({ productsSelection: arr });
            } else {
                setSelectedKeys([]);
            }
        } else if (!dependentProperties) {
            if (selectedKeys.filter((item) => item.key === key).length !== 0) {
                const tempSelectedKeys = selectedKeys.filter(
                    (item) => item.key !== key && item.key !== 0 && item.key !== parentProperty
                );
                setQueryStateData({ productsSelection: tempSelectedKeys });
            } else {
                let count = 0;
                let parent = options.filter((item) => item.key === parentProperty)[0];
                let temp = parent.dependentProperties as number[];
                temp.forEach((ele) => {
                    if (selectedKeys.filter((item) => item.key === ele).length !== 0) {
                        count = count + 1;
                    }
                });
                if (count + 1 === temp.length) {
                    const tempSelectedKeys = [
                        ...selectedKeys,
                        options.filter((item) => item.key === key)[0],
                        options.filter((item) => item.key === parentProperty)[0]
                    ];
                    setQueryStateData({ productsSelection: tempSelectedKeys });
                } else {
                    const tempSelectedKeys = [...selectedKeys, options.filter((item) => item.key === key)[0]];
                    setQueryStateData({ productsSelection: tempSelectedKeys });
                }
            }
        } else {
            if (selectedKeys.filter((item) => item.key === key).length !== 0) {
                let arr = selectedKeys;
                dependentProperties.forEach((ele) =>
                    arr.splice(
                        arr.findIndex((element) => {
                            if (element.key === ele) return true;
                            return false;
                        }),
                        1
                    )
                );
                const tempSelectedKeys = arr.filter((ele) => ele.key !== key && ele.key !== 0);
                setQueryStateData({ productsSelection: tempSelectedKeys });
            } else {
                let arr: IOptionProps[] = [];
                dependentProperties.forEach((item) => {
                    if (selectedKeys.filter((it) => it.key === item).length === 0)
                        arr.push(options.filter((ele) => ele.key === item)[0]);
                });
                const tempSelectedKeys = [...selectedKeys.concat(arr), options.filter((item) => item.key === key)[0]];
                setQueryStateData({ productsSelection: tempSelectedKeys });
            }
        }
    };
    const isChecked = (ele: number) => {
        return selectedKeys.filter((item) => item.key === ele).length !== 0;
    };
    const onToggleShow = () => {
        if (!filterPanelDisabled) {
            setShowDropdown((value) => !value);
        }
    };
    const removeDuplicates = (arr: string[]) => {
        let uniqueArr = arr.filter((ele, index) => {
            return arr.indexOf(ele) === index;
        });
        return uniqueArr;
    };
    const getLabel = () => {
        if (selectedKeys?.filter((ele) => ele.key === 0).length !== 0 || selectedKeys?.length === 0) {
            return StringConstants.UPPERCASE_ALL;
        } else {
            let arr: string[] = [];
            selectedKeys.forEach((item) => {
                let element = options.filter((ele) => ele.key === item.key)[0];
                if (element?.parentProperty) {
                    if (selectedKeys.filter((ele) => ele.key === element?.parentProperty).length !== 0)
                        arr.push(options.filter((it) => it.key === element?.parentProperty)[0].text + ' - All');
                    else arr.push(element?.text);
                } else {
                    arr.push(element?.text + ' - All');
                }
            });
            let res = removeDuplicates(arr);
            return res.join(', ');
        }
    };

    const formatIntoOptions = (data: DeviceCategory[]) => {
        let arr: IOptionProps[] = [{ key: 0, text: StringConstants.UPPERCASE_ALL }];
        let index = 1;
        data?.forEach((item) => {
            let childArray: number[] = [];
            let parentIndex = index;
            index = index + 1;
            item.devices.forEach((ele) => {
                childArray.push(index);
                index = index + 1;
            });
            arr.push({ key: parentIndex, text: item.name, dependentProperties: childArray });
            let tempIndex = parentIndex;
            tempIndex = tempIndex + 1;
            item.devices.forEach((ele) => {
                arr.push({ key: tempIndex, text: ele.name, parentProperty: parentIndex, type: ele.id });
                tempIndex += 1;
            });
        });
        return arr;
    };
    const checkEquality = (arr1: IOptionProps[], arr2: IOptionProps[]) => {
        if (arr1?.length !== arr2?.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            if (arr2.filter((it) => it.key === arr1[i].key).length === 0) {
                return false;
            }
        }
        return true;
    };
    useDropDownDismiss(dropDownRef, onToggleShow, filterPanelDisabled);

    useEffect(() => {
        if (!isLoading && !isError) {
            setOptions(formatIntoOptions(data));
        }
    }, [data, isLoading, isError]);

    useEffect(() => {
        if (!showDropdown) setSearchValue('');
    }, [showDropdown]);
    useEffect(() => {
        if (searchValue.trim() !== '') {
            let arr: IOptionProps[] = [];
            options.forEach((item) => {
                if (item.text.toLowerCase().includes(searchValue.trim().toLowerCase())) {
                    arr.push(item);
                }
            });
            setSearchedResults(arr);
        }
    }, [searchValue]);

    useEffect(() => {
        if (showDropdown && !isLoading && !isError && options.length > 0 && selectedKeys.length === 0) {
            setSelectedKeys(options.slice(0));
        }
    }, [showDropdown, isLoading, isError, options]);
    useEffect(() => {
        if (
            options.length !== 0 &&
            selectedKeys?.length !== 0 &&
            checkEquality(
                selectedKeys,
                options.filter((it) => it.key !== 0)
            )
        ) {
            setSelectedKeys(options.slice(0));
        }
    }, [options, selectedKeys]);

    useEffect(() => {
        if (selectedKeys.length > 0 && selectedKeys.length !== options.length) {
            setFilterActive(true);
        } else {
            setFilterActive(false);
        }
    }, [selectedKeys]);

    return (
        <div className={`products-filter filter-cont ${isListView ? 'list-view-filter' : ''}`}>
            <Text className="filter-heading">Products & Models {(selectedKeys.length > 0 && selectedKeys.length !== options.length) && <FontIcon iconName="LocationDot" className="filter-active-icon" />}</Text>

            <div
                className={`placeholder-title row-flex-box align-center ${filterPanelDisabled ? 'filter-disabled' : ''}`}
                role="button"
                aria-label="Filter by products and models"
                aria-expanded={showDropdown}
                aria-disabled={filterPanelDisabled}
                tabIndex={0}
                data-testid="pdt-filter-open-btn-test"
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    keyPress(e, onToggleShow);
                }}
                onClick={() => onToggleShow()}>
                <Text className="placeholder-label">{getLabel()}</Text> <Icon iconName="ChevronDown" />
            </div>

            {showDropdown && !isLoading && (
                <div ref={dropDownRef} className="dropdown-cont">
                    <div
                        role="button"
                        aria-label="Close products and models filter"
                        aria-expanded={showDropdown}
                        aria-disabled={filterPanelDisabled}
                        tabIndex={0}
                        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                            keyPress(e, onToggleShow);
                        }}
                        data-testid="pdt-filter-close-btn-test"
                        onClick={() => onToggleShow()}
                        className="row-flex-box align-center justify-sb dropdown-header">
                        <Text>Products & Models</Text>
                        <Icon iconName="ChevronUp" />
                    </div>
                    {isError && (
                        <div role="alert" data-testid="products-filter-error">
                            Products could not be loaded. Refresh the page to try again.
                        </div>
                    )}
                    <div className="search-bar-cont" hidden={isError}>
                        {selectedKeys.filter((ele) => ele.key === 0).length !== 0 ? (
                            <div className="tags-cont">
                                <div className="tag-item">
                                    <Text>All products</Text>{' '}
                                    <Icon
                                        onKeyPress={(e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                                onChange(0);
                                            }
                                        }}
                                        data-testid="tag-all-test"
                                        onClick={() => onChange(0)}
                                        iconName="Cancel"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="tags-cont">
                                {selectedKeys.map((ele) => (
                                    <div className="tag-item">
                                        <Text>
                                            {ele.text}
                                            {ele.dependentProperties ? ' - All' : ''}
                                        </Text>
                                        <Icon
                                            tabIndex={0}
                                            onKeyPress={(e: React.KeyboardEvent) => {
                                                if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                                    onChange(
                                                        ele.key as number,
                                                        ele.dependentProperties as number[],
                                                        ele.parentProperty as number
                                                    );
                                                }
                                            }}
                                            onClick={() =>
                                                onChange(
                                                    ele.key as number,
                                                    ele.dependentProperties as number[],
                                                    ele.parentProperty as number
                                                )
                                            }
                                            iconName="Cancel"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        <SearchBox
                            placeholder="Search"
                            tabIndex={0}
                            data-testid="search-field-test"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (e?.target?.value) setSearchValue(e?.target?.value);
                                else setSearchValue('');
                            }}
                        />
                    </div>
                    {!isError && (searchValue.trim() !== '' ? (
                        <div>
                            {' '}
                            {searchedResults.map((item, index) => (
                                <div
                                    data-testid={`dropdown-item-test-${index}`}
                                    onKeyPress={(e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                            onChange(
                                                item.key as number,
                                                item.dependentProperties as number[],
                                                item.parentProperty as number
                                            );
                                        }
                                    }}
                                    className={`dropdown-item ${
                                        item.parentProperty &&
                                        searchedResults.filter((ele) => ele.key === item.parentProperty).length !== 0
                                            ? 'child-item'
                                            : ''
                                    }  ${item.dependentProperties ? 'parent-item' : ''}`}>
                                    <Checkbox
                                        tabIndex={0}
                                        label={item.text}
                                        checked={isChecked(item.key as number)}
                                        onChange={() =>
                                            onChange(
                                                item.key as number,
                                                item.dependentProperties as number[],
                                                item.parentProperty as number
                                            )
                                        }
                                    />
                                </div>
                            ))}{' '}
                        </div>
                    ) : (
                        <div>
                            {options.map((item, index) => (
                                <div
                                    data-testid={`dropdown-item-test-${index}`}
                                    onKeyPress={(e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                            onChange(
                                                item.key as number,
                                                item.dependentProperties as number[],
                                                item.parentProperty as number
                                            );
                                        }
                                    }}
                                    className={`dropdown-item ${item.parentProperty ? 'child-item' : ''}  ${
                                        item.dependentProperties ? 'parent-item' : ''
                                    }`}>
                                    <Checkbox
                                        data-testid={`dropdown-checkbox-test-${index}`}
                                        tabIndex={0}
                                        label={item.text}
                                        checked={isChecked(item.key as number)}
                                        onChange={() =>
                                            onChange(
                                                item.key as number,
                                                item.dependentProperties as number[],
                                                item.parentProperty as number
                                            )
                                        }
                                    />
                                </div>
                            ))}{' '}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
const useDropDownDismiss = (ref: React.MutableRefObject<HTMLDivElement>, onToggleShow: Function, filterPanelDisabled: boolean) => {
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onToggleShow();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, filterPanelDisabled]);
};
export default ProductsFilter;
